import { Kanban, LayoutList } from "lucide-react";
import { cn } from "@/lib/utils";
import { useCrmStore } from "@/stores/crm.store";
import type { ViewMode } from "@/stores/crm.store";

const MODES: { value: ViewMode; label: string; Icon: typeof Kanban }[] = [
  { value: "board", label: "Tablero", Icon: Kanban },
  { value: "table", label: "Tabla", Icon: LayoutList },
];

/**
 * Board ⇄ table switch. The two modes render the same filtered set, so switching
 * never clears what's selected — only the view mode changes, and it is the one
 * filter-bar-adjacent value that persists across sessions.
 *
 * Bound to Pipeline de Prospectos' store by default; /pedidos passes its own
 * `value`/`onChange`, so the two pages remember their views independently.
 */
export function ViewModeToggle({
  value,
  onChange,
}: {
  value?: ViewMode;
  onChange?: (mode: ViewMode) => void;
} = {}): React.JSX.Element {
  const storeViewMode = useCrmStore((s) => s.viewMode);
  const setStoreViewMode = useCrmStore((s) => s.setViewMode);
  const viewMode = value ?? storeViewMode;
  const setViewMode = onChange ?? setStoreViewMode;

  return (
    <div className="flex gap-1 rounded-lg bg-ink-050 p-1" role="group" aria-label="Modo de vista">
      {MODES.map(({ value: mode, label, Icon }) => (
        <button
          key={mode}
          type="button"
          onClick={() => setViewMode(mode)}
          aria-pressed={viewMode === mode}
          className={cn(
            "flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors cursor-pointer",
            viewMode === mode
              ? "bg-white font-semibold text-ink-900 shadow-sm"
              : "text-ink-500 hover:text-ink-700",
          )}
        >
          <Icon size={14} />
          {label}
        </button>
      ))}
    </div>
  );
}
